// packages
import cx from 'classnames';
import { entries, map, Map, set, Set } from 'd3-collection';
import { GeoPath, GeoIdentityTransform, GeoProjection } from 'd3-geo';
import { scaleLinear, ScaleLinear } from 'd3-scale';
import { Feature, Point } from 'geojson';
import { Component, h } from 'preact';
import bboxPolygon from '@turf/bbox-polygon';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

// components
import CanvasLayer from './CanvasLayer';
import MapLegend from './MapLegend';
import Places from './Places';
import Portal from './Portal';
import SvgLayer from './SvgLayer';
import Tooltip from './Tooltip';
import TooltipTable from './TooltipTable';

// internal
import clamp from '../utils/clamp';
import * as colors from '../colors';
import find from '../utils/find';
import getPoint from '../utils/get-point';
import prepareTopoJSON from '../utils/prepare-topojson';
import { Topology } from 'topojson-specification';
import sumValuesNoKey from '../utils/sum-values-no-key';

interface LayeredMapProps {
  projection: GeoProjection | GeoIdentityTransform;
  topology: Topology;
  precinctsReporting: number;
  totalVotes: number;
  candidatesRender: { [key: string]: any };
}

interface LayeredMapState {
  activeGeometry: Feature | null;
  candidatesOnMap: Set;
  geos: any;
  width: number;
  height: number;
  x: number;
  y: number;
  includesNoResult: boolean;
  includesTie: boolean;
}

const tooltipPush = 20;

const relocateTooltip = (tooltip, x, y) => {
  const { height, width } = tooltip.getBoundingClientRect();

  const innerWidth = window.innerWidth;
  const innerHeight = window.innerHeight;

  const left = clamp(
    x - width / 2,
    tooltipPush,
    innerWidth - width - tooltipPush
  );
  const top =
    innerHeight > y + tooltipPush + height
      ? y + tooltipPush
      : y - height - tooltipPush;

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
};

class LayeredMap extends Component<LayeredMapProps, LayeredMapState> {
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  tooltip!: HTMLElement;
  candidateMap!: Map<any>;
  countyMap!: Map<any>;
  embedType: string;
  candidatesRender: { [key: string]: any };

  constructor() {
    super();

    this.state = {
      activeGeometry: null,
      candidatesOnMap: set(),
      geos: {},
      width: 0,
      height: 0,
      includesNoResult: false,
      includesTie: false,
      x: 0,
      y: 0,
    };

    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onDocumentTouch = this.onDocumentTouch.bind(this);
    this.getTooltipRef = this.getTooltipRef.bind(this);
    this.canvasRenderer = this.canvasRenderer.bind(this);

    this.xScale = scaleLinear();
    this.yScale = scaleLinear();
  }

  getTooltipRef(el: HTMLElement) {
    this.tooltip = el;
  }

  setUpCountyMap(counties: any) {
    this.countyMap = map(counties, d => d.fips);
  }

  setUpCandidateMap(candidates: any) {
    this.candidateMap = map(candidates, d => d.id);
  }

  onMouseMove(event: MouseEvent) {
    const { clientX: x, clientY: y, target } = event;
    const point = getPoint(target as HTMLElement, x, y);

    const coords = [this.xScale.invert(point[0]), this.yScale.invert(point[1])];

    const { collection, index } = this.state.geos;

    const found = index
      .search(...coords, ...coords)
      .map((i: number) => collection.features[i]);

    const activeGeometry = find(
      found,
      (feature: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>) =>
        booleanPointInPolygon(coords, feature)
    );

    let activeCounty = null;

    if (activeGeometry) {
      const { fips } = activeGeometry.properties;
      activeCounty = this.countyMap.get(fips);
    }

    this.setState({ activeCounty, activeGeometry }, () =>
      relocateTooltip(this.tooltip, x, y)
    );
  }

  onMouseLeave() {
    this.setState({ activeCounty: null, activeGeometry: null });
  }

  onDocumentTouch(e) {
    if (!this.base.contains(e.target)) this.onMouseLeave();
  }

  canvasRenderer(ctx: CanvasRenderingContext2D, geoPath: GeoPath) {
    const { geos, width, height } = this.state;

    const { candidatesRender, embedType } = this.props;

    ctx.clearRect(0, 0, width, height);

    if (!geos.collection) return;

    const candidatesOnMap = set();
    let includesNoResult = false;
    let includesTie = false;

    geos.collection.features.forEach((feature: Feature) => {
      const { fips } = feature.properties;
      const county = this.countyMap.get(fips);

      let fillStyle, lean

      const totalVotes = sumValuesNoKey(county.results);

      if (county.total_votes <= 0) {
        includesNoResult = true;
        fillStyle = colors.NEUTRAL;
      } else {
        const sortedResults = entries(county.results).sort(
          (a, b) => b.value - a.value
        );

        const leader = sortedResults[0];
        const second = sortedResults[1];

        if (leader.value === second.value) {
          includesTie = true;
          fillStyle = colors.TIE;
        } else {
          const leadingId = leader.key;
          candidatesOnMap.add(leadingId);

          const leader_pct = leader.value/totalVotes
          const second_pct = second.value/totalVotes

          const margin = (leader_pct-second_pct)

          if (margin.toFixed(2) < 0.10) {
            lean = '_LEAN'
          } else {
            lean = ''
          }

          const { party } = this.candidateMap.get(leadingId);
          fillStyle = colors[party.toUpperCase() + lean];
        }
      }

      ctx.beginPath();
      geoPath(feature);

      ctx.fillStyle = fillStyle;
      ctx.fill();
      ctx.closePath();
    });

    ctx.beginPath();
    geoPath(geos.collection);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255, 255, 255, .6)';
    ctx.lineWidth = 0.8;

    ctx.stroke();
    ctx.closePath();

    geos.roads.features.forEach((feature: Feature) => {
      if (feature.properties.level !== 'Interstate') return;
      ctx.beginPath();
      geoPath(feature);

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (feature.properties.level === 'Interstate') {
        ctx.strokeStyle = 'rgba(0, 0, 0, .12)';
        ctx.lineWidth = 1;
      } else {
        ctx.strokeStyle = 'rgba(0, 0, 0, .08)';
        ctx.lineWidth = 0.8;
      }

      ctx.stroke();
      ctx.closePath();
    });

    let places_mappable = geos.places;

    let embedTypeUsable = 'none';

    if (typeof embedType !== 'undefined') {
      embedTypeUsable = embedType;
    }

    if (embedTypeUsable.includes('tribcast')) {
      places_mappable.features = geos.places.features.filter(
        i =>
          i['properties']['name'] == 'Fort Worth' ||
          i['properties']['name'] == 'Dallas' ||
          i['properties']['name'] == 'San Antonio' ||
          i['properties']['name'] == 'Austin' ||
          i['properties']['name'] == 'Houston'
      );
    }

    geos.places = places_mappable;

    geos.places.features.forEach((feature: Feature<Point>) => {
      const [x, y] = geoPath.centroid(feature);

      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2, true);
      ctx.strokeStyle = 'rgba(33, 33, 33, .75)';
      ctx.lineWidth = 1.25;
      ctx.stroke();
      ctx.closePath();
    });

    geos.urban_areas.features.forEach((feature: Feature) => {
      ctx.beginPath();
      geoPath(feature);

      ctx.fillStyle = 'rgba(153, 160, 175, 0.2)';
      ctx.fill();
      ctx.closePath();
    });

    this.setState({ candidatesOnMap, includesNoResult, includesTie });
  }

  componentWillReceiveProps(nextProps) {
    const { candidates, counties } = nextProps;

    this.setUpCandidateMap(candidates);
    this.setUpCountyMap(counties);
  }

  getContainerDimensions() {
    const base = this.base!;
    const rawWidth = base.offsetWidth;

    this.setState({ width });
  }

  componentWillUnmount() {
    document.removeEventListener('touchstart', this.onDocumentTouch, false);
  }

  componentDidMount() {
    const { candidates, counties } = this.props;

    this.setUpCandidateMap(candidates);
    this.setUpCountyMap(counties);

    const base = this.base!;
    const rawWidth = Math.floor(base.clientWidth);

    const { topology } = this.props;
    const geos = prepareTopoJSON(topology);

    const { bbox, heightRatio } = geos;
    const rawHeight = Math.floor(rawWidth * heightRatio);

    const width = rawWidth;
    const height = rawHeight;

    this.xScale.domain([bbox[0], bbox[2]]).range([0, width]);
    this.yScale.domain([bbox[1], bbox[3]]).range([height, 0]);

    this.props.projection.fitSize([width, height], bboxPolygon(bbox));

    document.addEventListener('touchstart', this.onDocumentTouch, false);

    this.setState({ geos, width, height });
  }

  render() {
    const {
      candidates,
      projection,
      precinctsReporting,
      totalVotes,
      candidatesRender,
      embedType,
    } = this.props;
    const {
      activeCounty,
      activeGeometry,
      candidatesOnMap,
      geos,
      includesNoResult,
      includesTie,
      width,
      height,
      x,
      y,
    } = this.state;
    

    return (
      <div
        class="layers-map"
        style={{
          boxSizing: 'border-box',
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
        onMouseMove={this.onMouseMove}
        onMouseLeave={this.onMouseLeave}
      >
        <SvgLayer
          width={width}
          height={height}
          projection={projection}
          render={({ geoPath }) => (
            <g>
              {geos.places && (
                <Places geoPath={geoPath} places={geos.places.features} />
              )}
              {activeGeometry && (
                <path
                  fill="none"
                  stroke="black"
                  stroke-width="2"
                  d={geoPath(activeGeometry) || undefined}
                />
              )}
            </g>
          )}
        />
        <CanvasLayer
          totalPrecincts={precinctsReporting}
          totalVotes={totalVotes}
          projection={projection}
          width={width}
          height={height}
          render={this.canvasRenderer}
        />
        <MapLegend
          candidates={candidates.filter(({ id }: { id: string }) =>
            candidatesOnMap.has(id)
          )}
          includesNoResult={includesNoResult}
          includesTie={includesTie}
        />
        <Tooltip
          class={cx('tooltip map__tooltip', {
            invisible: !Boolean(activeCounty),
          })}
          tooltipRef={this.getTooltipRef}
        >
          {activeCounty && (
            <TooltipTable
              candidatesRender={candidatesRender}
              candidatesMap={this.candidateMap}
              county={activeCounty}
            />
          )}
        </Tooltip>
      </div>
    );
  }
}

export default LayeredMap;
